import React, { useCallback, useState } from 'react';

import { SignModal } from '../ConnectPage/components/SignModal/SignModal';
import { t } from 'i18next';

import { ForgeModal } from 'modules/nfts/components/modals/forge-modal';
import { MintModal } from 'modules/nfts/components/modals/mint-modal';

import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { Header } from 'shared/components/sections/header/header';
import AnimatedTransition from 'shared/components/ui/AnimatedTransition/AnimatedTransition';
import { InfiniteMaxProgressBar } from 'shared/components/ui/progress-bar/progress-bar';
import { MovesEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useShowSignModal } from 'shared/hooks/useShowSignModal';
import { useQuestsStore } from 'shared/store/questsStore';

import { LevelLine } from './components/level-line/level-line';
import { TopNFTSPreview } from './components/top-nfts-preview/top-nfts-preview';
import { ForgeCard, MAX_FORGE_BATCH, MintCard, NFT_IMAGES, ORDER, TierKey } from './constants';
import { findTierId } from './utils/utils';

export const NftCabinet: React.FC = () => {
  const { isConnected } = useAccount();
  const { showSignModal } = useShowSignModal();
  const [mintModal, setMintModal] = useState<{
    card: MintCard | null;
    open: boolean;
  }>({
    card: null,
    open: false,
  });
  const [forgeModal, setForgeModal] = useState<{
    card: ForgeCard | null;
    open: boolean;
  }>({
    card: null,
    open: false,
  });

  const {
    pointBalance,
    nftBalances,
    setMinted,
    setMinting,
    setForgeSuccess,
    setForgeError,
    setMintError,
  } = useQuestsStore();

  const handleMint = useCallback(
    (move: MovesEnum, collection: TierKey) => {
      const tierId = findTierId(move, collection);
      setMintModal({
        card: {
          image: NFT_IMAGES[collection][move],
          move,
          tierId,
        },
        open: true,
      });
    },
    [setMintModal, findTierId],
  );

  const handleForge = useCallback(
    (move: MovesEnum, collection: TierKey) => {
      if (collection === 'none') return;
      const collectionIndex = ORDER.indexOf(collection);
      if (collectionIndex <= 0) return; // nothing to forge from

      const prevKey = ORDER[collectionIndex - 1];
      const tierId = findTierId(move, collection) - 3; // previous tier id when forging
      const prevCount = nftBalances?.[prevKey]?.[move]?.count ?? 0;
      setForgeModal({
        card: {
          imageFrom: NFT_IMAGES[prevKey][move],
          imageTo: NFT_IMAGES[collection][move],
          move,
          collection,
          prevCollection: prevKey,
          prevCount,
          tierId,
        },
        open: true,
      });
    },
    [setForgeModal, nftBalances],
  );

  const handleCloseForgeModal = useCallback(() => {
    setForgeModal({ card: null, open: false });
    setForgeSuccess(null);
    setForgeError(null);
  }, [setForgeModal, setForgeSuccess, setForgeError]);

  const handleCloseMintModal = useCallback(() => {
    setMintModal({ card: null, open: false });
    setMinted(false);
    setMinting(false);
    setMintError(null);
  }, [setMintModal, setMinted, setMinting, setMintError]);

  if (!isConnected) {
    return (
      <ConnectWallet title={'common.connectWallet'} description={'pages.dailyQuests.connectDesc'} />
    );
  }
  return (
    <>
      <div className="flex min-h-screen flex-col items-center pb-10">
        <Header className="h-20 max-w-screen-2xl px-4 pt-3 lg:mx-auto 2xl:px-0" />
        <div className="flex w-full max-w-[720px] flex-col px-4">
          <TopNFTSPreview />
          <InfiniteMaxProgressBar value={Number(pointBalance ?? 0)} showAttention />
          <p className="mb-2 pt-1 text-center text-md font-normal text-[#A1B3D3] md:mb-4 md:pt-3 md:text-base">
            {t('nft.play')}
          </p>
          {ORDER.map((collection) => (
            <LevelLine
              key={collection}
              collection={collection}
              onMint={handleMint}
              onForge={handleForge}
            />
          ))}
        </div>
        <ForgeModal
          cardToForge={forgeModal.card?.imageTo}
          cardFromForge={forgeModal.card?.imageFrom}
          tierId={forgeModal.card?.tierId}
          move={forgeModal.card?.move}
          collection={forgeModal.card?.collection}
          prevCollection={forgeModal.card?.prevCollection}
          max={Math.min(MAX_FORGE_BATCH, forgeModal.card?.prevCount ?? 0)}
          open={forgeModal.open}
          onClose={handleCloseForgeModal}
        />
        <MintModal
          cardToMint={mintModal.card?.image}
          move={mintModal.card?.move}
          open={mintModal.open}
          onClose={handleCloseMintModal}
          max={Number(pointBalance)}
        />
      </div>
      <AnimatedTransition show={showSignModal}>
        <SignModal />
      </AnimatedTransition>
    </>
  );
};
